import { lazy, Suspense, useEffect, useLayoutEffect, useRef } from 'react'
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { HelmetProvider } from 'react-helmet-async'
import { Navbar } from '@/components/layout/Navbar'
import { Footer } from '@/components/layout/Footer'
import { Breadcrumbs } from '@/components/layout/Breadcrumbs'
import { WalletModal } from '@/components/checkout/WalletModal'
import { VapiAssistant } from '@/components/ai/VapiAssistant'
import { Toaster } from '@/components/ui/Toast'
import { Spinner } from '@/components/ui/Spinner'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { useAuthStore } from '@/stores/authStore'
import { useWalletStore } from '@/stores/walletStore'
import { toast } from '@/stores/toastStore'

// Lazy-loaded pages
const Landing = lazy(() => import('@/pages/Landing'))
const Catalog = lazy(() => import('@/pages/Catalog'))
const Marketplace = lazy(() => import('@/pages/Marketplace'))
const ProductDetail = lazy(() => import('@/pages/ProductDetail'))
const Contracts = lazy(() => import('@/pages/Contracts'))
const ContractDetail = lazy(() => import('@/pages/ContractDetail'))
const Bundles = lazy(() => import('@/pages/Bundles'))
const BundleDetail = lazy(() => import('@/pages/BundleDetail'))
const Checkout = lazy(() => import('@/pages/Checkout'))
const Learn = lazy(() => import('@/pages/Learn'))
const Article = lazy(() => import('@/pages/Article'))
const Dashboard = lazy(() => import('@/pages/Dashboard'))
const AgentDashboard = lazy(() => import('@/pages/AgentDashboard'))
const CheckoutSuccess = lazy(() => import('@/pages/CheckoutSuccess'))
const Profile = lazy(() => import('@/pages/Profile'))
const Register = lazy(() => import('@/pages/Register'))
const NotFound = lazy(() => import('@/pages/NotFound'))
const Terms = lazy(() => import('@/pages/legal/Terms'))
const Privacy = lazy(() => import('@/pages/legal/Privacy'))
const Refund = lazy(() => import('@/pages/legal/Refund'))
const Pricing = lazy(() => import('@/pages/Pricing'))
const Contact = lazy(() => import('@/pages/Contact'))
const Sell = lazy(() => import('@/pages/Sell'))
const SellerUpload = lazy(() => import('@/pages/SellerUpload'))
const SellerUploadStatus = lazy(() => import('@/pages/SellerUploadStatus'))
const OpenMarketplace = lazy(() => import('@/pages/OpenMarketplace'))
const VipDocs = lazy(() => import('@/pages/VipDocs'))

// Admin — lazy-loaded. Auth-gated so SEO isn't a concern, and most public
// visitors will never load these chunks. AdminLayout stays eager so the
// /admin gate renders without a flash, but everything inside is split.
import AdminLayout from '@/pages/admin/AdminLayout'
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'))
const ManageProducts = lazy(() => import('@/pages/admin/ManageProducts'))
const ProductFormPage = lazy(() => import('@/pages/admin/ProductFormPage'))
const DriveLibrary = lazy(() => import('@/pages/admin/DriveLibrary'))
const GoogleSetup = lazy(() => import('@/pages/admin/GoogleSetup'))
const ManageContracts = lazy(() => import('@/pages/admin/ManageContracts'))
const ContractFormPage = lazy(() => import('@/pages/admin/ContractFormPage'))
const ManageBundles = lazy(() => import('@/pages/admin/ManageBundles'))
const BundleFormPage = lazy(() => import('@/pages/admin/BundleFormPage'))
const ManageCarousel = lazy(() => import('@/pages/admin/ManageCarousel'))
const ManageOrders = lazy(() => import('@/pages/admin/ManageOrders'))
const ManageEducation = lazy(() => import('@/pages/admin/ManageEducation'))
const ArticleFormPage = lazy(() => import('@/pages/admin/ArticleFormPage'))
const Analytics = lazy(() => import('@/pages/admin/Analytics'))
const AdminSettings = lazy(() => import('@/pages/admin/Settings'))
const Treasury = lazy(() => import('@/pages/admin/Treasury'))
const ManagePricing = lazy(() => import('@/pages/admin/ManagePricing'))
const PricingFormPage = lazy(() => import('@/pages/admin/PricingFormPage'))
const Monitoring = lazy(() => import('@/pages/admin/Monitoring'))
const ManageSellerUploads = lazy(() => import('@/pages/admin/ManageSellerUploads'))

function ScrollToTop() {
  const { pathname, hash } = useLocation()
  // useLayoutEffect runs synchronously before paint, so the user never sees the
  // page render at the previous scroll position before snapping to the top.
  // Skip the snap when the URL has a hash fragment (e.g. /terms#section-3) so
  // anchor links still work.
  useLayoutEffect(() => {
    if (hash) return
    // 'instant' avoids the smooth-scroll defined in `html { scroll-behavior }`,
    // which would otherwise animate every navigation.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior })
  }, [pathname, hash])
  return null
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Spinner size="lg" />
    </div>
  )
}

function AppLayout() {
  const checkSession = useAuthStore((s) => s.checkSession)
  const login = useAuthStore((s) => s.login)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isLoading = useAuthStore((s) => s.isLoading)
  const walletAddress = useWalletStore((s) => s.address)
  const walletChainId = useWalletStore((s) => s.chainId)
  const isConnected = useWalletStore((s) => s.isConnected)
  const isRebinding = useWalletStore((s) => s.isRebinding)
  const navigate = useNavigate()
  const signInAttempted = useRef<string | null>(null)

  useEffect(() => {
    // Try to restore session — retry with backoff if API isn't ready yet
    // (covers the case where the frontend loads before the API server is up)
    let cancelled = false
    const tryCheck = async (attempt: number) => {
      try {
        await checkSession()
      } catch (_err) {
        if (cancelled) return
        // Retry up to 3 times with increasing delays (2s, 4s, 8s)
        if (attempt < 3) {
          const delay = 2000 * Math.pow(2, attempt)
          setTimeout(() => tryCheck(attempt + 1), delay)
        } else {
          console.warn('[App] Session check failed after 3 attempts — user will need to sign in manually')
        }
      }
    }
    tryCheck(0)
    return () => { cancelled = true }
  }, [checkSession])

  // Auto-trigger SIWE sign-in when wallet is connected but user is not authenticated.
  // Runs after the WalletModal closes (separate interaction from the connect popup).
  useEffect(() => {
    if (
      isConnected &&
      !isRebinding &&
      !isAuthenticated &&
      !isLoading &&
      walletAddress &&
      walletChainId &&
      signInAttempted.current !== walletAddress
    ) {
      signInAttempted.current = walletAddress
      login(walletAddress, Number(walletChainId))
        .then((result) => {
          if (result) {
            navigate('/register')
          } else {
            toast.success('Signed in successfully')
          }
        })
        .catch((err: unknown) => {
          const msg = err instanceof Error ? err.message : 'Sign in failed'
          toast.error('Authentication failed', msg)
        })
    }
  }, [isConnected, isRebinding, isAuthenticated, isLoading, walletAddress, walletChainId, login, navigate])

  return (
    <>
      <Navbar />
      {/* pt-16 clears the fixed 64px navbar for every page below. Individual
          pages should NOT add their own navbar offset — just their own
          breathing room (e.g. pt-6) on top of this. */}
      <div className="pt-16">
        <Breadcrumbs />
        <Suspense fallback={<PageLoader />}>
          <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/catalog" element={<Catalog />} />
          <Route path="/catalog/:slug" element={<ProductDetail />} />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/marketplace/:slug" element={<ProductDetail />} />
          <Route path="/contracts" element={<Contracts />} />
          <Route path="/contracts/:slug" element={<ContractDetail />} />
          <Route path="/bundles" element={<Bundles />} />
          <Route path="/bundles/:slug" element={<BundleDetail />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/checkout/:itemType/:itemId" element={<ErrorBoundary><Checkout /></ErrorBoundary>} />
          {/* Legacy redirect: /checkout?type=X&item=Y still lands here */}
          <Route path="/checkout" element={<ErrorBoundary><Checkout /></ErrorBoundary>} />
          <Route path="/learn" element={<Learn />} />
          <Route path="/learn/:slug" element={<Article />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/agent/dashboard" element={<AgentDashboard />} />
          <Route path="/my-products" element={<Navigate to="/profile?tab=products" replace />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/sell" element={<Sell />} />
          <Route path="/seller-upload" element={<SellerUpload />} />
          <Route path="/seller-upload/status/:id" element={<SellerUploadStatus />} />
          <Route path="/open-marketplace" element={<OpenMarketplace />} />
          <Route path="/vip" element={<VipDocs />} />

          {/* Legal */}
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/refund" element={<Refund />} />

          <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </div>
      <Footer />

      {/* Global overlays — wallet modal has its own boundary to prevent
           wallet rejections from crashing the entire app */}
      <ErrorBoundary><WalletModal /></ErrorBoundary>
      <ErrorBoundary><VapiAssistant /></ErrorBoundary>
      <Toaster />
    </>
  )
}

export default function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ScrollToTop />
          <ErrorBoundary>
            <Routes>
              {/* Admin — no Navbar or Footer */}
              <Route path="/admin/*" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="products" element={<ManageProducts />} />
                <Route path="products/new" element={<ProductFormPage />} />
                <Route path="products/:id/edit" element={<ProductFormPage />} />
                <Route path="drive" element={<DriveLibrary />} />
                <Route path="google-setup" element={<GoogleSetup />} />
                <Route path="contracts" element={<ManageContracts />} />
                <Route path="contracts/new" element={<ContractFormPage />} />
                <Route path="contracts/:id/edit" element={<ContractFormPage />} />
                <Route path="bundles" element={<ManageBundles />} />
                <Route path="bundles/new" element={<BundleFormPage />} />
                <Route path="bundles/:id/edit" element={<BundleFormPage />} />
                <Route path="carousel" element={<ManageCarousel />} />
                <Route path="orders" element={<ManageOrders />} />
                <Route path="treasury" element={<Treasury />} />
                <Route path="education" element={<ManageEducation />} />
                <Route path="education/new" element={<ArticleFormPage />} />
                <Route path="education/:id/edit" element={<ArticleFormPage />} />
                <Route path="monitoring" element={<Monitoring />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="pricing" element={<ManagePricing />} />
                <Route path="pricing/new" element={<PricingFormPage />} />
                <Route path="pricing/:id/edit" element={<PricingFormPage />} />
                <Route path="seller-uploads" element={<ManageSellerUploads />} />
              </Route>

              {/* Register — full-page, no Navbar/Footer */}
              <Route path="/register" element={<Suspense fallback={<PageLoader />}><Register /></Suspense>} />

              {/* All other routes — with Navbar + Footer */}
              <Route path="*" element={<AppLayout />} />
            </Routes>
          </ErrorBoundary>
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  )
}
